import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

// Throttle for connection quality checks (shared cache within module)
let lastQualityCheckTs = 0;
let cachedQuality: 'excellent' | 'good' | 'poor' | 'offline' = 'offline';
interface PendingOperation {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  table: string;
  data: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  dependsOn?: string[]; // IDs de operaciones que deben ejecutarse primero
  checksum: string;
}

interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: number;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'offline';
  syncErrors: string[];
}

/**
 * Sistema de sincronización offline para garantizar integridad de datos
 * Maneja conexiones pobres y asegura que no se pierda información
 */
export const useOfflineSync = () => {
  const { user, selectedCompany } = useAuth();
  const { toast } = useToast();

  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingCount: 0,
    lastSyncTime: 0,
    connectionQuality: 'excellent',
    syncErrors: []
  });

  const [pendingOperations, setPendingOperations] = useState<PendingOperation[]>([]);

  // Generar checksum para verificar integridad
  const generateChecksum = useCallback(async (data: any): Promise<string> => {
    const jsonString = JSON.stringify(data, Object.keys(data).sort());
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  // Evaluar calidad de conexión
  const assessConnectionQuality = useCallback(async (): Promise<'excellent' | 'good' | 'poor' | 'offline'> => {
    if (!navigator.onLine) return 'offline';

    const now = Date.now();
    if (now - lastQualityCheckTs < 60000) {
      return cachedQuality;
    }

    const startTime = now;
    try {
      // Test de ping simple a Supabase (con timeout)
      const result = await Promise.race([
        supabase.from('companies').select('id').limit(1),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('timeout')), 5000)
        )
      ]);

      const responseTime = Date.now() - startTime;

      let quality: 'excellent' | 'good' | 'poor' | 'offline';
      if ((result as any)?.error) quality = 'offline';
      else if (responseTime < 500) quality = 'excellent';
      else if (responseTime < 1500) quality = 'good';
      else quality = 'poor';

      cachedQuality = quality;
      lastQualityCheckTs = Date.now();
      return quality;
    } catch (error) {
      cachedQuality = 'offline';
      lastQualityCheckTs = Date.now();
      return 'offline';
    }
  }, []);

  // Actualizar estado de conexión
  const updateConnectionStatus = useCallback(async () => {
    const quality = await assessConnectionQuality();
    const isOnline = quality !== 'offline';

    setSyncStatus(prev => ({
      ...prev,
      isOnline,
      connectionQuality: quality
    }));

    // Si volvimos online, intentar sincronizar
    if (isOnline && pendingOperations.length > 0) {
      setTimeout(processPendingOperations, 1000);
    }

    return { isOnline, quality };
  }, [pendingOperations.length]);

  // Guardar operación para sincronización posterior
  const queueOperation = useCallback(async (
    type: 'CREATE' | 'UPDATE' | 'DELETE',
    table: string,
    data: any,
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' = 'MEDIUM',
    dependsOn?: string[]
  ): Promise<string> => {
    const operationId = crypto.randomUUID();
    const checksum = await generateChecksum(data);

    const operation: PendingOperation = {
      id: operationId,
      type,
      table,
      data: {
        ...data,
        // Agregar metadatos de contexto
        _offline_context: {
          user_id: user?.id,
          company_id: selectedCompany?.id,
          created_offline: true,
          queue_timestamp: Date.now()
        }
      },
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: priority === 'CRITICAL' ? 10 : 5,
      priority,
      dependsOn,
      checksum
    };

    setPendingOperations(prev => {
      const updated = [...prev, operation];
      // Ordenar por prioridad y timestamp
      updated.sort((a, b) => {
        const priorityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return a.timestamp - b.timestamp;
      });
      return updated;
    });

    setSyncStatus(prev => ({
      ...prev,
      pendingCount: prev.pendingCount + 1
    }));

    // Guardar en localStorage como backup
    try {
      const backupOps = JSON.parse(localStorage.getItem(`offline_ops_${user?.id}`) || '[]');
      backupOps.push(operation);
      localStorage.setItem(`offline_ops_${user?.id}`, JSON.stringify(backupOps));
    } catch (error) {
      console.error('Error guardando operación en localStorage:', error);
    }

    console.log(`📦 Operación en cola: ${type} ${table} (${priority})`);

    // Mostrar toast informativo
    toast({
      title: "📦 Operación en Cola",
      description: `${type} guardado para sincronizar cuando mejore la conexión`,
      duration: 3000
    });

    return operationId;
  }, [user?.id, selectedCompany?.id, generateChecksum, toast]);

  // Procesar operaciones pendientes
  const processPendingOperations = useCallback(async () => {
    if (syncStatus.isSyncing || pendingOperations.length === 0) return;

    console.log(`🔄 Procesando ${pendingOperations.length} operaciones pendientes...`);

    setSyncStatus(prev => ({ ...prev, isSyncing: true, syncErrors: [] }));

    const processedOps: string[] = [];
    const failedOps: string[] = [];
    const errors: string[] = [];

    // Procesar operaciones en orden de dependencia
    const processOperation = async (operation: PendingOperation): Promise<boolean> => {
      try {
        // Verificar dependencias
        if (operation.dependsOn?.length) {
          const dependenciesResolved = operation.dependsOn.every(depId => 
            processedOps.includes(depId)
          );
          if (!dependenciesResolved) {
            return false; // Esperar dependencias
          }
        }

        console.log(`⚡ Ejecutando ${operation.type} en ${operation.table}`);

        let result;
        switch (operation.type) {
          case 'CREATE':
            result = await (supabase as any).from(operation.table).insert(operation.data);
            break;
          case 'UPDATE':
            const { _offline_context, ...updateData } = operation.data;
            result = await (supabase as any).from(operation.table)
              .update(updateData)
              .eq('id', operation.data.id);
            break;
          case 'DELETE':
            result = await (supabase as any).from(operation.table)
              .delete()
              .eq('id', operation.data.id);
            break;
        }

        if (result?.error) {
          throw result.error;
        }

        // Verificar integridad después de la operación
        if (operation.type !== 'DELETE' && result?.data?.[0]) {
          const newChecksum = await generateChecksum(result.data[0]);
          console.log(`✅ Operación ${operation.id} completada con integridad verificada`);
        }

        processedOps.push(operation.id);
        return true;

      } catch (error: any) {
        console.error(`❌ Error procesando operación ${operation.id}:`, error);
        
        operation.retryCount++;
        errors.push(`${operation.table} ${operation.type}: ${error.message}`);

        if (operation.retryCount < operation.maxRetries) {
          console.log(`🔄 Reintentando operación ${operation.id} (${operation.retryCount}/${operation.maxRetries})`);
          return false; // Mantener para reintentar
        } else {
          console.error(`💥 Operación ${operation.id} falló definitivamente después de ${operation.maxRetries} intentos`);
          failedOps.push(operation.id);
          
          // Para operaciones críticas, mantener en cola indefinidamente
          if (operation.priority === 'CRITICAL') {
            operation.retryCount = 0; // Resetear contador para operaciones críticas
            return false;
          }
          
          return true; // Remover de la cola
        }
      }
    };

    // Procesar todas las operaciones
    const remainingOps: PendingOperation[] = [];
    
    for (const operation of pendingOperations) {
      const processed = await processOperation(operation);
      if (!processed) {
        remainingOps.push(operation);
      }
    }

    // Actualizar estado
    setPendingOperations(remainingOps);
    
    setSyncStatus(prev => ({
      ...prev,
      isSyncing: false,
      pendingCount: remainingOps.length,
      lastSyncTime: Date.now(),
      syncErrors: errors
    }));

    // Actualizar localStorage
    try {
      localStorage.setItem(`offline_ops_${user?.id}`, JSON.stringify(remainingOps));
    } catch (error) {
      console.error('Error actualizando localStorage:', error);
    }

    // Mostrar resultados
    if (processedOps.length > 0) {
      console.log(`✅ ${processedOps.length} operaciones sincronizadas exitosamente`);
      toast({
        title: "✅ Sincronización Completa",
        description: `${processedOps.length} operaciones procesadas correctamente`,
        duration: 5000
      });
    }

    if (failedOps.length > 0) {
      console.log(`❌ ${failedOps.length} operaciones fallaron permanentemente`);
      toast({
        title: "⚠️ Algunas operaciones fallaron",
        description: `${failedOps.length} operaciones no pudieron completarse`,
        variant: "destructive",
        duration: 8000
      });
    }

  }, [syncStatus.isSyncing, pendingOperations, user?.id, generateChecksum, toast]);

  // Operación segura que maneja automáticamente online/offline
  const safeExecute = useCallback(async <T,>(
    operation: () => Promise<T>,
    fallbackData: {
      type: 'CREATE' | 'UPDATE' | 'DELETE';
      table: string;
      data: any;
      priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    }
  ): Promise<{ success: boolean; data?: T; operationId?: string; offline?: boolean }> => {
    
    const { isOnline, quality } = await updateConnectionStatus();

    // Si la conexión es pobre o no hay conexión, usar modo offline
    if (!isOnline || quality === 'poor') {
      const operationId = await queueOperation(
        fallbackData.type,
        fallbackData.table,
        fallbackData.data,
        fallbackData.priority
      );
      
      return { 
        success: true, 
        operationId, 
        offline: true 
      };
    }

    // Intentar operación online
    try {
      const result = await operation();
      return { success: true, data: result, offline: false };
    } catch (error: any) {
      console.error('Error en operación online, cambiando a modo offline:', error);
      
      // Si falla, usar modo offline como backup
      const operationId = await queueOperation(
        fallbackData.type,
        fallbackData.table,
        fallbackData.data,
        'HIGH' // Prioridad alta para operaciones que fallaron online
      );
      
      return { 
        success: true, 
        operationId, 
        offline: true 
      };
    }
  }, [updateConnectionStatus, queueOperation]);

  // Cargar operaciones pendientes al iniciar
  useEffect(() => {
    if (!user?.id) return;

    try {
      const savedOps = JSON.parse(localStorage.getItem(`offline_ops_${user.id}`) || '[]');
      if (savedOps.length > 0) {
        setPendingOperations(savedOps);
        setSyncStatus(prev => ({ ...prev, pendingCount: savedOps.length }));
        console.log(`📦 Cargadas ${savedOps.length} operaciones pendientes del localStorage`);
      }
    } catch (error) {
      console.error('Error cargando operaciones pendientes:', error);
    }
  }, [user?.id]);

  // Monitoreo automático de conexión y sincronización
  useEffect(() => {
    // Verificar conexión cada 60 segundos
    const connectionInterval = setInterval(updateConnectionStatus, 60000);
    
    // Intentar sincronizar cada 2 minutos
    const syncInterval = setInterval(() => {
      if (syncStatus.isOnline && pendingOperations.length > 0) {
        processPendingOperations();
      }
    }, 120000);

    // Listeners de eventos de conectividad
    const handleOnline = () => {
      console.log('🌐 Conexión restaurada, iniciando sincronización...');
      updateConnectionStatus();
      setTimeout(processPendingOperations, 2000);
    };

    const handleOffline = () => {
      console.log('📵 Conexión perdida, activando modo offline');
      setSyncStatus(prev => ({ 
        ...prev, 
        isOnline: false, 
        connectionQuality: 'offline' 
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Sincronizar al enfocar la ventana (usuario regresa)
    const handleFocus = () => {
      updateConnectionStatus();
      if (pendingOperations.length > 0) {
        setTimeout(processPendingOperations, 1000);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(connectionInterval);
      clearInterval(syncInterval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [updateConnectionStatus, processPendingOperations, syncStatus.isOnline, pendingOperations.length]);

  // Limpiar operaciones completadas al cerrar sesión
  const clearPendingOperations = useCallback(() => {
    setPendingOperations([]);
    setSyncStatus(prev => ({ ...prev, pendingCount: 0 }));
    if (user?.id) {
      localStorage.removeItem(`offline_ops_${user.id}`);
    }
  }, [user?.id]);

  return {
    syncStatus,
    pendingOperations,
    queueOperation,
    processPendingOperations,
    safeExecute,
    updateConnectionStatus,
    clearPendingOperations
  };
};